/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRejectedAccountProducts, fetchPendingAccountProducts, fetchApprovedAccountProducts } from 'state/thunks/account';
// utils
// material
import { Page, Content, InContentMessage, Button, displayModal } from '@insticator/insticator-ui';
import PageHeader from "material/structure/pageheader/pageheader";
import PageContent from "material/structure/pagecontent/pagecontent";
import SiteFilter from 'material/sitefilter/sitefilter';
import LoadingState from 'material/states/loading/loading';
// content
import Space from 'material/space/space';
import CodeSetup from './codesetup/codesetup';
// styles
import injectSheet from "react-jss";
import styles from './productmanagerconfig_styles';
import { getProductManagerConfig } from 'state/thunks/productmanager';
import ProductAccess from './productaccess/productaccess';
import { useIsAdminUser } from 'utils/session';
import { revertDraftToLive } from 'state/slices/productmanager';
import PageSetupV2 from './pagesetup/pagesetupv2';
import TestSetupModal from './testsetupmodal/testsetupmodal';
import ConfirmPublishModal from './confirmpublishmodal/confirmpublishmodal';
import { isControlBarDraftLiveEqual, isProductGroupDraftLiveEqual } from 'utils/productgroup';
import { isObjectArrayEqual } from 'utils/isObjectArrayEqual';


/* ========== ~~~~~~~~~~ MODERATION CONFIG PAGE ~~~~~~~~~~ ========== */
const ProductManagerConfigV2  = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const { siteUUID } = useSelector(state => state.session);
  const { productManagerConfig, tempProductManagerConfig, productManagerConfigChanges } = useSelector(state => state.productManager)
  const { accountProducts } = useSelector((state) => state.account);
  const noProductAccess = useMemo(() => accountProducts.length === 0, [accountProducts])

  const [isLoading,setIsLoading] = useState(true)
  const isAdmin = useIsAdminUser()

  const fetchProductManagerConfig = useCallback(async()=>{
    setIsLoading(true)
    await dispatch(getProductManagerConfig({ siteId: siteUUID }))
    setIsLoading(false)
  }, [dispatch, siteUUID])

  useEffect(() => {
    if(siteUUID) fetchProductManagerConfig()
  }, [fetchProductManagerConfig, siteUUID]) 

  const isProductManagerConfigChanged = useMemo(() => {
    return !(JSON.stringify(productManagerConfig) === JSON.stringify(tempProductManagerConfig));
  },[productManagerConfig, tempProductManagerConfig])


  const accountUUID = useSelector((state) => state.session.accountUUID)

  useEffect(() => {
    if(!accountUUID) return
    dispatch(fetchApprovedAccountProducts(accountUUID))
    dispatch(fetchPendingAccountProducts(accountUUID))
    dispatch(fetchRejectedAccountProducts(accountUUID))
  }, [accountUUID, dispatch])

  const showTestSetupModal = () => dispatch(displayModal(true, <TestSetupModal />, false))

  const showPublishConfirmModal = () => dispatch(displayModal(true, <ConfirmPublishModal />, false))

  const handleRevert = useCallback(async () => {
    await dispatch(revertDraftToLive())
  }, [dispatch])

  const isGroupEdited = productManagerConfigChanges.includes('groupConfigList')
  const isCommentsCounterChangedOnly = productManagerConfigChanges.includes('commentsCounterEnabled') && !productManagerConfigChanges.includes('groupConfigList')
  const isGroupTested = productManagerConfigChanges.includes('groupConfigTested') && productManagerConfigChanges.length === 1
  const isProductGroupEmpty = tempProductManagerConfig.groupConfigList.length === 0

  const isDraftLiveDifferent = useMemo(() => {
    let isChanged = 0
    tempProductManagerConfig.groupConfigList.forEach((group) => {
      if(isProductGroupDraftLiveEqual(group)) return
      isChanged += 1
    })
    return !!isChanged || isProductGroupEmpty
  }, [isProductGroupEmpty, tempProductManagerConfig.groupConfigList])

  const isGroupNameEmpty = tempProductManagerConfig.groupConfigList.filter(group => !group.draft.label).length > 0

  const isGroupNameDuplicate = useMemo(() => {
    let isGroupNameDuplicated = 0

    const groupNames = tempProductManagerConfig.groupConfigList.map(group => group.draft.label)

    tempProductManagerConfig.groupConfigList.forEach((group, index)=> {
      groupNames.forEach((existingNames, idx) => {
        if(existingNames === group.draft.label && idx !== index) isGroupNameDuplicated += 1
      }) 
    })
    return isGroupNameDuplicated > 0
  }, [tempProductManagerConfig])

  const noDeviceSelected = useMemo(() => tempProductManagerConfig.groupConfigList.filter(group => group.draft.deviceType === null).length > 0, [tempProductManagerConfig])

  const noProductsEnabled = useMemo(() => tempProductManagerConfig.groupConfigList.filter(group => !group.draft.showCommenting && !group.draft.showTrendingNow && !group.draft.showArticleSentiment).length > 0, [tempProductManagerConfig])

  const isProductGroupNotCreated = !tempProductManagerConfig.id
  
  const isProductGroupDraftOnly = tempProductManagerConfig.groupConfigList.filter(group => group.current && group.current.label === null).length === tempProductManagerConfig.groupConfigList.length

  const isProductGroupHasLive = tempProductManagerConfig.groupConfigList.filter(group => !isProductGroupDraftLiveEqual(group) && group.groupId && group.current.label).length > 0

  const noControlBarConfig = useMemo(() => {
    const isGroupTriggerControlBar = tempProductManagerConfig.groupConfigList.filter(grp => grp.draft.trigger === 'CONTROL_BAR').length > 0
    return !tempProductManagerConfig.controlBarConfig && isGroupTriggerControlBar
  },[tempProductManagerConfig])
  
  const groupButtonTriggerNoConfig = useMemo(() => {
    const isGroupButtonNoConfig = tempProductManagerConfig.groupConfigList.filter(grp => grp.draft.trigger === 'BUTTON' && !grp.draft.buttonConfig).length > 0
    return isGroupButtonNoConfig
  },[tempProductManagerConfig])

  const savedGroupListEmpty = productManagerConfig.groupConfigList.length === 0 && tempProductManagerConfig.groupConfigList.length === 0
  
  const isGroupInvalid = isGroupNameEmpty || isGroupNameDuplicate || noDeviceSelected || noProductsEnabled || noControlBarConfig || groupButtonTriggerNoConfig

  const isCommentsCounterChanged = tempProductManagerConfig.commentsCounterEnabled !== productManagerConfig.commentsCounterEnabled && !isGroupEdited

  const isGroupHasControlBar = tempProductManagerConfig.groupConfigList.filter(grp => grp.draft.trigger === 'CONTROL_BAR').length > 0

  const controlBarConfigDraftLiveEqual = isControlBarDraftLiveEqual(tempProductManagerConfig.controlBarConfig)

  const isProductGroupAllLive = tempProductManagerConfig.groupConfigList.length > 0 &&  tempProductManagerConfig.groupConfigList.filter(group => isProductGroupDraftLiveEqual(group)).length === tempProductManagerConfig.groupConfigList.length && !!controlBarConfigDraftLiveEqual

  const showGlobalSave = (isProductManagerConfigChanged || isDraftLiveDifferent || isGroupEdited) || (isGroupHasControlBar ? !controlBarConfigDraftLiveEqual : false)
  
  const isPublishDisable = useMemo(() => {
    let isDisable = false
    if((!isGroupTested && isGroupEdited)) isDisable = true
    if(isProductGroupAllLive) isDisable = true
    if(isGroupInvalid) isDisable = true
    if(savedGroupListEmpty) isDisable = true
    if(isCommentsCounterChanged) isDisable = false
    return isDisable
  }, [isGroupTested, isGroupEdited, isProductGroupAllLive, isGroupInvalid, savedGroupListEmpty, isCommentsCounterChanged])

  const globalSave = showGlobalSave && (
    <div className={'globalsave'}>
      <Space justifyContent={'flex-end'}>
        {<Button 
          variant={'none'} 
          color={'light'} 
          name={'revert'} 
          text={'Revert'} 
          disabled={isProductGroupAllLive || isProductGroupDraftOnly} 
          handleClick={() => handleRevert()} 
        />}
        <Button 
          variant={'primary'} 
          name={'savedraft'} 
          text={'Test Setup'}
          disabled={isProductGroupAllLive || isGroupTested || isCommentsCounterChangedOnly || isGroupInvalid || savedGroupListEmpty || !isPublishDisable}
          handleClick={() => showTestSetupModal()} 
        />
        <Button 
          variant={'none'} 
          color={'compliment'} 
          name={'setascurrent'} 
          text={'Publish Changes'}
          disabled={isPublishDisable}
          handleClick={() => showPublishConfirmModal()} 
        />
      </Space>
    </div>
  )

  return (
    <Page name={'productmanagerpage'} klass={`productmanagerpage ${props.classes.productmanagerpage}`}>
      <SiteFilter hideTestPageButton />
      <Content>
        <InContentMessage />
        <PageHeader title={'Product Manager'} />
        <PageContent>
        { isLoading  ?
          <LoadingState />
          :
          <>
            <ProductAccess />
            { isAdmin &&
              <div className={`disabled-${noProductAccess}`}>
                <CodeSetup />
                <PageSetupV2 />
                <div style={{position: 'absolute', bottom: 0, left: '0px', width: '100%'}}>
                {globalSave}
                </div>
              </div>
            } 
          </>
        }
        </PageContent>
      </Content>
    </Page>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ProductManagerConfigV2